import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CSS/profile.css';
import { useAuth } from '../hooks/useAuth';
import { useOrders } from '../hooks/useOrders';
import { useAddresses } from '../hooks/useAddresses';
import { useWishlist } from '../hooks/useWishlist';
import { useCart } from '../hooks/useCart';
import { useBids } from '../hooks/useBids';
import { useAuthStore } from '../stores/authStore';
import { apiService } from '../services/api';
import { useNotifications } from '../hooks/useNotifications';
import { useConversations } from '../hooks/useConversations';
import { useShippingZones } from '../hooks/useShipping';
import { LoadingSpinner, EmptyState } from '../componnents/UI';
import BidModal from '../componnents/BidModal/BidModal';
import { formatPrice, formatDate, getProductImageUrl, getProductName, getProductPrices, getProductId } from '../utils/helpers';
import { normalizeOffer } from '../utils/offerNormalize';
import MyBidsList from '../componnents/profile/MyBidsList';
import ProfileCompletionBanner from '../componnents/ProfileCompletionBanner/ProfileCompletionBanner';
import { getProfileCompletion, getProfileTabForMissingField, getDisplayCurrentLocation } from '../utils/profileCompletion';
import { 
  User, Package, MapPin, Settings, LogOut, ChevronRight,
  Star, Truck, Shield, Trash2, Edit, Plus, X, Heart,
  Clock, CheckCircle, XCircle, Eye, Camera, ShoppingCart, MessageCircle, Loader2
} from 'lucide-react';

function formatDateForInput(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const deepLinkOrderId = searchParams.get('orderId');
  const profileCompletePrompt = searchParams.get('complete') === '1';
  const fileInputRef = useRef(null);
  const { t } = useTranslation('profile');
  
  const { user, logout, updateUser } = useAuthStore();
  const { isUpdatingProfile, changePassword, isChangingPassword, deleteAccount, profile, isLoadingProfile } = useAuth();
  const {
    orders,
    isLoading: isLoadingOrders,
    cancelOrderAsync,
    isCancellingOrder,
    deleteOrderAsync,
    isDeletingOrder,
  } = useOrders();
  const { addresses, isLoading: isLoadingAddresses, addAddress, updateAddress, deleteAddress, setDefaultAddress, isAddingAddress, isUpdatingAddress, isDeletingAddress } = useAddresses();

  const displayUser = profile || user;
  const displayLocation = getDisplayCurrentLocation(displayUser, addresses);
  const { wishlist, isLoading: isLoadingWishlist, removeFromWishlist, isRemovingFromWishlist } = useWishlist();
  const { addToCart, isAddingToCart } = useCart();
  const { myBids, isLoadingBids, addBidToCartAsync, isAddingBidToCart } = useBids();
  const { notifications, markAsRead, deleteNotification } = useNotifications();
  const { conversations } = useConversations();
  
  const [activeTab, setActiveTab] = useState(initialTab);

  const unreadOrderNotifications = useMemo(
    () => notifications.filter((n) => n.type === 'order' && !n.isRead).length,
    [notifications]
  );

  const unreadOffersCount = useMemo(() => {
    const fromNotifications = notifications.filter(
      (n) => (n.type === 'offer' || n.type === 'bid') && !n.isRead
    ).length;
    const fromConversations = (conversations || []).reduce(
      (sum, c) => sum + (c.unreadByBuyer || 0),
      0
    );
    return Math.max(fromNotifications, fromConversations);
  }, [notifications, conversations]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderToCancel, setOrderToCancel] = useState(null);

  useEffect(() => {
    if (!deepLinkOrderId || isLoadingOrders) return;
    const match = (orders || []).find(
      (order) => String(order._id || order.id) === String(deepLinkOrderId)
    );
    if (match) {
      setActiveTab('orders');
      setSelectedOrder(match);
    }
  }, [deepLinkOrderId, orders, isLoadingOrders]);

  useEffect(() => {
    if (!profileCompletePrompt) return;
    const { missing } = getProfileCompletion(displayUser, addresses);
    if (missing.length === 0) return;

    const targetTab = getProfileTabForMissingField(missing[0]);
    setActiveTab(targetTab);
    setSearchParams({ tab: targetTab, complete: '1' });

    if (targetTab === 'settings') {
      setShowEditProfileModal(true);
    } else if (targetTab === 'addresses') {
      setShowAddressModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileCompletePrompt]);

  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [orderFilter, setOrderFilter] = useState('all');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedBidProduct, setSelectedBidProduct] = useState(null);
  
  // Pagination state for Orders
  const [ordersPage, setOrdersPage] = useState(1);
  const [allOrders, setAllOrders] = useState([]);
  const [isLoadingMoreOrders, setIsLoadingMoreOrders] = useState(false);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  
  // Pagination state for Bids
  const [bidsPage, setBidsPage] = useState(1);
  const [allBids, setAllBids] = useState([]);
  const [isLoadingMoreBids, setIsLoadingMoreBids] = useState(false);
  const [hasMoreBids, setHasMoreBids] = useState(true);
  
  // Use refs to track previous values and prevent infinite loops
  const prevOrdersRef = useRef(null);
  const prevBidsRef = useRef(null);
  
  // Update orders when data changes - only when content actually changes
  useEffect(() => {
    // Create a stable reference for comparison
    const ordersKey = orders?.length ? `${orders.length}-${orders[0]?._id || orders[0]?.id || ''}` : 'empty';
    
    // Only update if orders actually changed (by comparing length and first item ID)
    if (prevOrdersRef.current !== ordersKey && orders && ordersPage === 1) {
      // Sort by date (most recent first) before setting
      const sortedOrders = [...orders].sort((a, b) => {
        const dateA = new Date(a.createdAt || a.date || 0).getTime();
        const dateB = new Date(b.createdAt || b.date || 0).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
      setAllOrders(sortedOrders);
      setHasMoreOrders(orders.length >= 20);
      prevOrdersRef.current = ordersKey;
    } else if ((!orders || orders.length === 0) && allOrders.length > 0) {
      // Clear if orders became empty
      setAllOrders([]);
      setHasMoreOrders(false);
      prevOrdersRef.current = 'empty';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, ordersPage]);
  
  // Update bids when data changes - only when content actually changes
  useEffect(() => {
    // Create a stable reference for comparison
    const bidsKey = myBids?.length ? `${myBids.length}-${myBids[0]?._id || myBids[0]?.id || ''}` : 'empty';
    
    // Only update if bids actually changed (by comparing length and first item ID)
    if (prevBidsRef.current !== bidsKey && myBids && bidsPage === 1) {
      setAllBids(myBids);
      setHasMoreBids(myBids.length >= 10);
      prevBidsRef.current = bidsKey;
    } else if ((!myBids || myBids.length === 0) && allBids.length > 0) {
      // Clear if bids became empty
      setAllBids([]);
      setHasMoreBids(false);
      prevBidsRef.current = 'empty';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBids, bidsPage]);

  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    nationality: '',
    currentLocation: '',
    gender: '',
    dateOfBirth: '',
  });

  useEffect(() => {
    if (!displayUser) return;
    setProfileForm({
      firstName: displayUser.firstName || displayUser.name?.split(' ')[0] || '',
      lastName: displayUser.lastName || displayUser.name?.split(' ').slice(1).join(' ') || '',
      email: displayUser.email || '',
      phoneNumber: displayUser.phoneNumber || displayUser.phone || '',
      nationality: displayUser.nationality || '',
      currentLocation: displayUser.currentLocation || getDisplayCurrentLocation(displayUser, addresses) || '',
      gender: displayUser.gender || '',
      dateOfBirth: formatDateForInput(displayUser.dateOfBirth),
    });
  }, [displayUser, addresses]);
  
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(false);
  
  // Address modal state
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressError, setAddressError] = useState(null);
  const [addressForm, setAddressForm] = useState({
    fullName: '',
    phoneNumber: '',
    homeAddress: '',
    city: '',
    neighborhood: '',
    state: '',
    postalCode: '',
    country: 'Congo',
    isDefault: false,
  });

  const { data: shippingZones = [], isLoading: isLoadingZones } = useShippingZones();

  const neighborhoodLabel = (code) => {
    if (!code) return '';
    const zone = shippingZones.find(
      (z) => z.code === code || z.id === code || z.name === code
    );
    return zone?.name || code;
  };

  const userName = displayUser?.name || `${displayUser?.firstName || ''} ${displayUser?.lastName || ''}`.trim() || displayUser?.email?.split('@')[0] || 'User';
  const userInitials = userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  // Order counts - use allOrders if available, otherwise use orders
  const ordersForCount = allOrders.length > 0 ? allOrders : (orders || []);
  const orderCounts = {
    all: ordersForCount.length,
    pending: ordersForCount.filter(o => {
      const status = (o.orderStatus || o.status || '').toLowerCase().replace(/\s/g, '');
      return status === 'pending';
    }).length,
    shipped: ordersForCount.filter(o => {
      const status = (o.orderStatus || o.status || '').toLowerCase().replace(/\s/g, '');
      return status === 'outfordelivery' || status === 'shipped';
    }).length,
    delivered: ordersForCount.filter(o => {
      const status = (o.orderStatus || o.status || '').toLowerCase();
      return status === 'complete' || status === 'delivered';
    }).length,
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert(t('accountMessages.imageTypeError'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('accountMessages.imageSizeError'));
      return;
    }

    setIsUploadingImage(true);
    try {
      // Create FormData with profilePicture key (same as mobile app)
      const formData = new FormData();
      formData.append('profilePicture', file, file.name);
      
      // Use updateUserProfile (same as mobile app st_cael)
      const response = await apiService.updateUserProfile(formData);
      
      if (response.status === 'success' && response.data) {
        // Update ONLY the profile image fields (don't spread entire user object)
        const newImageUrl = response.data.profileImageUrl || response.data.profilePicture;
        updateUser({ profileImageUrl: newImageUrl, profilePicture: newImageUrl });
        alert(t('accountMessages.imageUploadSuccess'));
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert(t('accountMessages.imageUploadFailed'));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!window.confirm(t('accountMessages.imageRemoveConfirm'))) {
      return;
    }

    setIsUploadingImage(true);
    try {
      const response = await apiService.removeProfilePicture();
      if (response.status === 'success') {
        // Update ONLY the profile image fields (don't spread entire user object)
        updateUser({ profileImageUrl: null, profilePicture: null });
        alert(t('accountMessages.imageRemoveSuccess'));
      }
    } catch (error) {
      console.error('Failed to remove profile picture:', error);
      alert(t('accountMessages.imageRemoveFailed'));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(false);

    if (
      !profileForm.firstName?.trim() ||
      !profileForm.lastName?.trim() ||
      !profileForm.phoneNumber?.trim() ||
      !profileForm.nationality?.trim()
    ) {
      setProfileError(t('addressesSection.requiredFields'));
      return;
    }

    if (!profileForm.currentLocation?.trim() && (!addresses || addresses.length === 0)) {
      setProfileError(t('profileCompletion.missingLocation'));
      return;
    }

    try {
      // Use PATCH with JSON for text data updates (not PUT with FormData)
      const result = await apiService.updateProfile({
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        phoneNumber: profileForm.phoneNumber,
        nationality: profileForm.nationality,
        currentLocation: profileForm.currentLocation,
        gender: profileForm.gender,
        dateOfBirth: profileForm.dateOfBirth || undefined,
      });
      
      if (result.status === 'success') {
        // Update local user state with the response data or form data
        updateUser({
          ...(result.data || {}),
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          phoneNumber: profileForm.phoneNumber,
          nationality: profileForm.nationality,
          currentLocation: profileForm.currentLocation,
          gender: profileForm.gender,
          dateOfBirth: profileForm.dateOfBirth
            ? new Date(`${profileForm.dateOfBirth}T00:00:00.000Z`).toISOString()
            : null,
          name: `${profileForm.firstName || ''} ${profileForm.lastName || ''}`.trim(),
        });
        setProfileSuccess(true);
        alert(t('accountMessages.profileUpdateSuccess'));
        setTimeout(() => {
          setShowEditProfileModal(false);
          const { missing } = getProfileCompletion(
            {
              ...displayUser,
              ...profileForm,
            },
            addresses
          );
          if (missing.includes('address')) {
            handleTabChange('addresses');
            setShowAddressModal(true);
          }
        }, 300);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setProfileError(error.message || t('accountMessages.profileUpdateFailed'));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('accountMessages.passwordMismatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError(t('accountMessages.passwordMin'));
      return;
    }

    try {
      await changePassword({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword
      });
      setPasswordSuccess(true);
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setShowPasswordModal(false), 2000);
    } catch (error) {
      setPasswordError(error.message || t('accountMessages.passwordChangeFailed'));
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      navigate('/');
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  const handleAddToCartFromWishlist = async (product) => {
    try {
      await addToCart({ productId: getProductId(product), quantity: 1 });
    } catch (error) {
      console.error('Failed to add to cart:', error);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      await removeFromWishlist(productId);
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
    }
  };

  // Address handlers
  const openAddAddressModal = () => {
    setEditingAddress(null);
    setAddressForm({
      fullName: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '',
      phoneNumber: user?.phoneNumber || user?.phone || '',
      homeAddress: '',
      city: '',
      neighborhood: '',
      state: '',
      postalCode: '',
      country: 'Congo',
      isDefault: addresses?.length === 0,
    });
    setAddressError(null);
    setShowAddressModal(true);
  };

  const openEditAddressModal = (address) => {
    setEditingAddress(address);
    setAddressForm({
      fullName: address.fullName || '',
      phoneNumber: address.phoneNumber || '',
      homeAddress: address.homeAddress || address.address || '',
      city: address.city || '',
      neighborhood: address.neighborhood || address.state || '',
      state: address.state || '',
      postalCode: address.postalCode || address.zipCode || '',
      country: address.country || '',
      isDefault: address.isDefault || false,
    });
    setAddressError(null);
    setShowAddressModal(true);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setAddressError(null);

    // Validation
    if (!addressForm.fullName || !addressForm.phoneNumber || !addressForm.homeAddress || !addressForm.city || !addressForm.country) {
      setAddressError(t('addressesSection.requiredFields'));
      return;
    }

    try {
      if (editingAddress) {
        // Update existing address
        await updateAddress({
          id: editingAddress._id || editingAddress.id,
          addressData: addressForm,
        });
        alert(t('addressesSection.updated'));
      } else {
        // Add new address
        await addAddress(addressForm);
        alert(t('addressesSection.added'));
      }
      setShowAddressModal(false);
      setEditingAddress(null);
    } catch (error) {
      console.error('Failed to save address:', error);
      setAddressError(error.message || t('addressesSection.saveFailed'));
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm(t('addressesSection.deleteConfirm'))) {
      return;
    }
    try {
      await deleteAddress(addressId);
    } catch (error) {
      console.error('Failed to delete address:', error);
      alert(t('addressesSection.deleteFailed'));
    }
  };

  // Helper function to normalize status (handles French/English)
  const normalizeStatus = (status) => {
    if (!status) return '';
    const normalized = String(status).toLowerCase().trim();
    // Map French to English equivalents
    const statusMap = {
      'en attente': 'pending',
      'accepté': 'accepted',
      'accepte': 'accepted',
      'rejeté': 'rejected',
      'rejete': 'rejected',
      'expiré': 'expired',
      'expire': 'expired',
      'annulé': 'cancelled',
      'annule': 'cancelled',
      'cancelled_by_buyer': 'cancelled',
      'cancelledbybuyer': 'cancelled',
      'livré': 'delivered',
      'livre': 'delivered',
      'complété': 'delivered',
      'complete': 'delivered',
      'refusé': 'rejected',
      'refuse': 'rejected',
    };
    return statusMap[normalized] || normalized;
  };

  const getStatusIcon = (status) => {
    const s = normalizeStatus(status).replace(/\s/g, '');
    if (s === 'pending') return <Clock size={16} className="status-icon pending" />;
    if (s === 'confirmed') return <CheckCircle size={16} className="status-icon confirmed" />;
    if (['outfordelivery', 'shipped'].includes(s)) return <Truck size={16} className="status-icon shipped" />;
    if (['complete', 'delivered'].includes(s)) return <CheckCircle size={16} className="status-icon delivered" />;
    if (s === 'cancelled') return <XCircle size={16} className="status-icon cancelled" />;
    return <Package size={16} />;
  };

  const getStatusText = (status) => {
    const s = (status || '').toLowerCase().replace(/\s/g, '');
    if (s === 'cancelled_by_buyer' || s === 'cancelledbybuyer') {
      return t('orderCancel.statusCancelledByBuyer');
    }
    const statusKeyMap = {
      pending: 'pending',
      confirmed: 'confirmed',
      outfordelivery: 'outForDelivery',
      shipped: 'shipped',
      complete: 'delivered',
      delivered: 'delivered',
      cancelled: 'cancelled',
    };
    const statusKey = statusKeyMap[s];
    if (statusKey) return t(`orderList.status.${statusKey}`);
    return status || t('orderList.status.unknown');
  };

  const canBuyerCancelOrder = (order) => {
    const raw = (order?.orderStatus || order?.status || '').toUpperCase().replace(/\s/g, '_');
    return raw === 'PENDING' || raw === 'WAITING_CONFIRMATION';
  };

  const handleConfirmCancelOrder = async () => {
    if (!orderToCancel) return;
    const orderId = orderToCancel._id || orderToCancel.id;
    if (!orderId) return;
    setCancelError('');
    try {
      await cancelOrderAsync({
        orderId,
        reason: cancelReason.trim() || undefined,
      });
      setOrderToCancel(null);
      setCancelReason('');
      setSelectedOrder(null);
      alert(t('orderCancel.cancelSuccess'));
    } catch (error) {
      setCancelError(error?.message || t('orderCancel.cancelFailed'));
    }
  };

  // Overview Tab
  const renderOverview = () => (
    <div className="profile-overview">
      <div className="profile-card">
        <div className="profile-info">
          <div className="avatar-container" onClick={() => fileInputRef.current?.click()}>
            {displayUser?.profileImageUrl || displayUser?.profilePicture ? (
              <img src={displayUser.profileImageUrl || displayUser.profilePicture} alt="Profile" className="avatar-image" />
            ) : (
              <span className="avatar-initials">{userInitials}</span>
            )}
            <div className="avatar-overlay">
              <Camera size={20} />
            </div>
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*" 
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            {isUploadingImage && <div className="upload-spinner"></div>}
          </div>
          <div className="user-details">
            <h2 className="user-name">{userName}</h2>
            <p className="user-email">{displayUser?.email}</p>
            <span className="membership-badge">{displayUser?.membershipLevel || t('overviewSection.regularMember')}</span>
          </div>
        </div>
        <button className="edit-profile-btn" onClick={() => setShowEditProfileModal(true)}>
          <Edit size={18} />
          {t('overviewSection.editProfile')}
        </button>
      </div>

      <div className="order-stats-section">
        <div className="section-header">
          <h3>{t('orderList.myOrders')}</h3>
          <button className="see-all-link" onClick={() => handleTabChange('orders')}>
            {t('orderList.seeAll')} <ChevronRight size={16} />
          </button>
        </div>
        <div className="order-stats">
          <button className="stat-item" onClick={() => { handleTabChange('orders'); setOrderFilter('shipped'); }}>
            <Truck size={24} />
            <span className="stat-count">{orderCounts.shipped}</span>
            <span className="stat-label">{t('orderList.stats.shipped')}</span>
          </button>
          <button className="stat-item" onClick={() => { handleTabChange('orders'); setOrderFilter('delivered'); }}>
            <Star size={24} />
            <span className="stat-count">{orderCounts.delivered}</span>
            <span className="stat-label">{t('orderList.stats.delivered')}</span>
          </button>
          <button className="stat-item" onClick={() => { handleTabChange('orders'); setOrderFilter('pending'); }}>
            <Clock size={24} />
            <span className="stat-count">{orderCounts.pending}</span>
            <span className="stat-label">{t('orderList.stats.pending')}</span>
          </button>
        </div>
      </div>

      <div className="quick-actions">
        <button className="action-item" onClick={() => handleTabChange('orders')}>
          <Package size={20} />
          <span>{t('orderList.orderHistory')}</span>
          <ChevronRight size={18} />
        </button>
        <button className="action-item" onClick={() => handleTabChange('wishlist')}>
          <Heart size={20} />
          <span>{t('overviewSection.myWishlist', { count: wishlist?.length || 0 })}</span>
          <ChevronRight size={18} />
        </button>
        <button className="action-item" onClick={() => handleTabChange('addresses')}>
          <MapPin size={20} />
          <span>{t('overviewSection.manageAddresses')}</span>
          <ChevronRight size={18} />
        </button>
        <button className="action-item" onClick={() => setShowPasswordModal(true)}>
          <Shield size={20} />
          <span>{t('overviewSection.changePassword')}</span>
          <ChevronRight size={18} />
        </button>
        <button className="action-item logout" onClick={handleLogout}>
          <LogOut size={20} />
          <span>{t('signOut')}</span>
        </button>
      </div>
    </div>
  );

  // Orders Tab
  const renderOrders = () => {
    // Use allOrders for display, but apply status filter first
    const ordersToFilter = allOrders.length > 0 ? allOrders : (orders || []);
    
    // Apply status filter
    const statusFilteredOrders = ordersToFilter.filter(order => {
      if (orderFilter === 'all') return true;
      const status = (order.orderStatus || order.status || '').toLowerCase().replace(/\s/g, '');
      if (orderFilter === 'pending') return status === 'pending';
      if (orderFilter === 'confirmed') return status === 'confirmed';
      if (orderFilter === 'shipped') return status === 'outfordelivery' || status === 'shipped';
      if (orderFilter === 'delivered') return status === 'complete' || status === 'delivered';
      return true;
    }).sort((a, b) => {
      // Sort by date (most recent first)
      const dateA = new Date(a.createdAt || a.date || 0).getTime();
      const dateB = new Date(b.createdAt || b.date || 0).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
    
    return (
      <div className="orders-section">
        <div className="orders-filter">
          {['all', 'pending', 'shipped', 'delivered'].map(filter => {
            const count = filter === 'all' 
              ? ordersToFilter.length 
              : ordersToFilter.filter(o => {
                  const status = (o.orderStatus || o.status || '').toLowerCase().replace(/\s/g, '');
                  if (filter === 'pending') return status === 'pending';
                  if (filter === 'shipped') return status === 'outfordelivery' || status === 'shipped';
                  if (filter === 'delivered') return status === 'complete' || status === 'delivered';
                  return false;
                }).length;
            
            return (
              <button
                key={filter}
                className={`filter-btn ${orderFilter === filter ? 'active' : ''}`}
                onClick={() => setOrderFilter(filter)}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
                {count > 0 && ` (${count})`}
              </button>
            );
          })}
        </div>

        {isLoadingOrders ? (
          <LoadingSpinner text="Loading orders..." />
        ) : statusFilteredOrders.length === 0 ? (
          <EmptyState
            icon="package"
            title="No orders found"
            description={orderFilter === 'all' ? "You haven't placed any orders yet" : `No ${orderFilter} orders`}
            actionText="Start Shopping"
            actionLink="/"
          />
        ) : (
          <div className="orders-list">
            {statusFilteredOrders.map((order, idx) => {
            const firstItem = order.items?.[0];
            const totalItems = order.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
            
            return (
              <div key={order.id || order._id || `order-${idx}-${order.createdAt || Date.now()}`} className="order-card">
                <div className="order-header">
                  <div className="order-id">
                    <span className="order-number">#{order.orderNumber || order.id}</span>
                    <span className="order-date">{formatDate(order.createdAt)}</span>
                  </div>
                  <div className={`order-status-badge ${(order.orderStatus || order.status || '').toLowerCase().replace(/\s/g, '-')}`}>
                    {getStatusIcon(order.orderStatus || order.status)}
                    <span>{getStatusText(order.orderStatus || order.status)}</span>
                  </div>
                </div>

                <div className="order-content">
                  {firstItem && (
                    <div className="order-product">
                      <img 
                        src={getProductImageUrl(firstItem.product || firstItem)} 
                        alt={firstItem.product?.name || 'Product'} 
                        className="order-product-image"
                      />
                      <div className="order-product-info">
                        <p className="product-name">{firstItem.product?.name || 'Product'}</p>
                        {order.items?.length > 1 && (
                          <p className="more-items">+{order.items.length - 1} more items</p>
                        )}
                        <p className="item-count">{totalItems} item{totalItems > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  )}
                  <div className="order-total">
                    <span className="total-label">{t('orderList.totalLabel')}</span>
                    <span className="total-amount">{formatPrice(order.finalPrice || order.totalPrice || order.total)}</span>
                    <span className="order-date" style={{ display: 'block', marginTop: '0.25rem' }}>
                      {getPaymentStatusLabel(order)}
                    </span>
                  </div>
                </div>

                <div className="order-actions">
                  <button className="order-action-btn primary" onClick={() => {
                    // Mark order as read when viewing details
                    const orderId = order._id || order.id;
                    const orderIdStr = String(orderId);
                    if (orderId) {
                      const orderNotifications = notifications.filter(
                        (n) => n.type === 'order' && 
                        !n.isRead && 
                        String(n.data?.orderId) === orderIdStr
                      );
                      
                      if (orderNotifications.length > 0) {
                        void Promise.all(
                          orderNotifications.map(async (notification) => {
                            try {
                              await markAsRead(notification._id);
                            } catch (error) {
                              console.error('Failed to mark/delete order notification:', error);
                            }
                          })
                        ).catch((error) => {
                          console.error('Error processing order notifications:', error);
                        });
                      }
                    }
                    setSelectedOrder(order);
                  }}>
                    <Eye size={16} />
                    {t('orderList.viewDetails')}
                  </button>
                  {(() => {
                    const orderStatus = order.orderStatus || order.status || '';
                    const normalizedStatus = normalizeStatus(orderStatus);
                    const isDelivered = ['delivered', 'complete', 'complété', 'livré', 'livre'].includes(normalizedStatus);
                    const canDeleteOrder = ['delivered', 'complete', 'complété', 'livré', 'livre', 'rejected', 'rejeté', 'rejete', 'refusé', 'refuse', 'cancelled', 'annulé', 'annule'].includes(normalizedStatus);
                    const showCancelOrder = canBuyerCancelOrder(order);

                    return (
                      <>
                        {showCancelOrder && (
                          <button
                            className="order-action-btn secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCancelError('');
                              setCancelReason('');
                              setOrderToCancel(order);
                            }}
                            disabled={isCancellingOrder}
                          >
                            <XCircle size={16} />
                            {t('orderCancel.cancelOrder')}
                          </button>
                        )}
                        {isDelivered && (
                          <button className="order-action-btn secondary">
                            <Star size={16} />
                            Leave Review
                          </button>
                        )}
                        {canDeleteOrder && (
                          <button
                            className="order-action-btn delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(t('orderList.deleteConfirm'))) {
                                const orderId = order._id || order.id;
                                // Wrap async operation to prevent unhandled promise rejection
                                (async () => {
                                  try {
                                    await deleteOrderAsync(orderId);
                                    alert('Order deleted successfully');
                                  } catch (error) {
                                    alert(error.message || 'Failed to delete order');
                                  }
                                })().catch(err => {
                                  console.error('Unexpected error deleting order:', err);
                                });
                              }
                            }}
                            disabled={isDeletingOrder}
                            title={t('orderList.deleteOrder')}
                          >
                            <Trash2 size={16} />
                            {t('orderList.deleteOrder')}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
        )}
        
        {/* Load More Button for Orders */}
        {hasMoreOrders && statusFilteredOrders.length > 0 && (
          <div className="load-more-container" style={{ textAlign: 'center', marginTop: '2rem', padding: '1rem' }}>
            <button
              className="load-more-btn"
              onClick={async () => {
                setIsLoadingMoreOrders(true);
                try {
                  const nextPage = ordersPage + 1;
                  const response = await apiService.getOrders({ page: nextPage, limit: 20 });
                  const newOrders = response.data || [];
                  // Merge and sort all orders by date (most recent first)
                  const mergedOrders = [...allOrders, ...newOrders].sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.date || 0).getTime();
                    const dateB = new Date(b.createdAt || b.date || 0).getTime();
                    return dateB - dateA; // Descending order (newest first)
                  });
                  setAllOrders(mergedOrders);
                  setOrdersPage(nextPage);
                  setHasMoreOrders(newOrders.length >= 20);
                } catch (err) {
                  console.error('Failed to load more orders:', err);
                  setHasMoreOrders(false);
                } finally {
                  setIsLoadingMoreOrders(false);
                }
              }}
              disabled={isLoadingMoreOrders}
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: 500,
                color: '#fff',
                backgroundColor: '#2563eb',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: isLoadingMoreOrders ? 'not-allowed' : 'pointer',
                opacity: isLoadingMoreOrders ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {isLoadingMoreOrders ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t('loadingMore', { ns: 'common' })}
                </>
              ) : (
                t('loadMore', { ns: 'common' })
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Wishlist Tab
  const renderWishlist = () => (
    <div className="wishlist-section">
      {isLoadingWishlist ? (
        <LoadingSpinner text={t('wishlistSection.loading')} />
      ) : !wishlist || wishlist.length === 0 ? (
        <EmptyState
          icon="wishlist"
          title={t('wishlistSection.emptyTitle')}
          description={t('wishlistSection.emptyDescription')}
          actionText="Browse Products"
          actionLink="/"
        />
      ) : (
        <div className="wishlist-grid">
          {wishlist.map((item) => {
            const product = item.product || item;
            const { price, comparePrice } = getProductPrices(product);
            const productId = getProductId(product);
            
            return (
              <div key={productId} className="wishlist-item">
                <button 
                  className="remove-wishlist-btn"
                  onClick={() => handleRemoveFromWishlist(productId)}
                  disabled={isRemovingFromWishlist}
                >
                  <X size={18} />
                </button>
                <Link to={`/product/${productId}`} className="wishlist-product-link">
                  <img 
                    src={getProductImageUrl(product)} 
                    alt={getProductName(product)} 
                    className="wishlist-product-image"
                  />
                  <div className="wishlist-product-info">
                    <h4 className="wishlist-product-name">{getProductName(product)}</h4>
                    <div className="wishlist-product-price">
                      <span className="current-price">{formatPrice(price)}</span>
                      {comparePrice && <span className="original-price">{formatPrice(comparePrice)}</span>}
                    </div>
                  </div>
                </Link>
                <button 
                  className="add-to-cart-btn"
                  onClick={() => handleAddToCartFromWishlist(product)}
                  disabled={isAddingToCart}
                >
                  <ShoppingCart size={16} />
                  Add to Cart
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const getPaymentStatusLabel = (order) => {
    const method = (order.paymentMethod || '').toUpperCase();
    const paymentStatus = (order.paymentStatus || '').toUpperCase();
    const orderStatus = (order.orderStatus || order.status || '').toUpperCase();

    if (method !== 'CASH_ON_DELIVERY') {
      return paymentStatus === 'COMPLETED' ? t('payment.paid') : t('payment.pending');
    }
    if (orderStatus !== 'COMPLETE') {
      return t('payment.cod');
    }
    return paymentStatus === 'COMPLETED' ? t('payment.codPaid') : t('payment.codAwaiting');
  };

  const markBidNotificationsRead = (bid) => {
    const bidId = bid._id || bid.id;
    const productId = getProductId(bid.product) || bid.productId;
    if (!bidId) return;

    const bidNotifications = notifications.filter(
      (n) =>
        n.type === 'bid' &&
        !n.isRead &&
        (String(n.data?.bidId) === String(bidId) ||
          (productId && String(n.data?.productId) === String(productId)))
    );

    if (bidNotifications.length === 0) return;

    void Promise.all(
      bidNotifications.map(async (notification) => {
        try {
          await markAsRead(notification._id);
        } catch (error) {
          console.error('Failed to mark bid notification:', error);
        }
      })
    ).catch((error) => {
      console.error('Error processing bid notifications:', error);
    });
  };

  const handleBidAddToCart = async (bid) => {
    const bidId = bid._id || bid.id;
    const productId = getProductId(bid.product) || bid.productId;
    try {
      await addBidToCartAsync({ bidId, productId });
      navigate('/carte');
    } catch (error) {
      alert(error.message || t('offers.addToCartFailed'));
    }
  };

  const handleLoadMoreBids = () => {
    setIsLoadingMoreBids(true);
    void (async () => {
      try {
        const nextPage = bidsPage + 1;
        const response = await apiService.getMyBids(null, nextPage, 10);
        const raw = response.data?.bids || response.data?.offers || [];
        const newBids = Array.isArray(raw)
          ? raw.map(normalizeOffer).filter(Boolean)
          : [];
        setAllBids((prev) => [...prev, ...newBids]);
        setBidsPage(nextPage);
        setHasMoreBids(newBids.length >= 10);
      } catch (err) {
        console.error('Failed to load more bids:', err);
        setHasMoreBids(false);
      } finally {
        setIsLoadingMoreBids(false);
      }
    })();
  };

  const renderBids = () => {
    const displayBids = allBids.length > 0 ? allBids : myBids || [];

    return (
      <MyBidsList
        bids={displayBids}
        isLoading={isLoadingBids}
        hasMore={hasMoreBids}
        isLoadingMore={isLoadingMoreBids}
        onLoadMore={handleLoadMoreBids}
        onAddToCart={handleBidAddToCart}
        isAddingBidToCart={isAddingBidToCart}
        onMarkNotificationsRead={markBidNotificationsRead}
      />
    );
  };

  // Addresses Tab
  const renderAddresses = () => (
    <div className="addresses-section">
      <div className="section-header">
        <h3>{t('addressesSection.title')}</h3>
        <button className="add-address-btn" onClick={openAddAddressModal}>
          <Plus size={18} />
          Add New
        </button>
      </div>

      {isLoadingAddresses ? (
        <LoadingSpinner text={t('addressesSection.loading')} />
      ) : addresses?.length === 0 ? (
        <EmptyState
          icon="location"
          title={t('addressesSection.emptyTitle')}
          description={t('addressesSection.emptyDescription')}
          actionText="Add Address"
          onAction={openAddAddressModal}
        />
      ) : (
        <div className="addresses-list">
          {addresses?.map((address, idx) => (
            <div key={address.id || address._id || `address-${idx}-${address.createdAt || Date.now()}`} className={`address-card ${address.isDefault ? 'default' : ''}`}>
              <div className="address-header">
                <span className="address-name">{address.fullName}</span>
                {address.isDefault && <span className="default-badge">{t('addressesSection.default')}</span>}
              </div>
              <p className="address-phone">{address.phoneNumber}</p>
              <p className="address-text">{address.homeAddress || address.address}</p>
              <p className="address-location">
                {address.city}
                {(address.neighborhood || address.state) &&
                  `, ${neighborhoodLabel(address.neighborhood || address.state)}`}
                {(address.postalCode || address.zipCode) && ` ${address.postalCode || address.zipCode}`}
              </p>
              <p className="address-country">{address.country}</p>
              <div className="address-actions">
                <button 
                  className="edit-address-btn"
                  onClick={() => openEditAddressModal(address)}
                >
                  <Edit size={16} />
                  Edit
                </button>
                {!address.isDefault && (
                  <button className="set-default-btn" onClick={() => setDefaultAddress(address.id || address._id)}>
                    Set as Default
                  </button>
                )}
                <button 
                  className="delete-address-btn"
                  onClick={() => handleDeleteAddress(address.id || address._id)}
                  disabled={isDeletingAddress}
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Settings Tab
  const renderSettings = () => (
    <div className="settings-section">
      <div className="settings-card">
        <h4>{t('settingsSection.basicInfo')}</h4>
        <p className="settings-subtitle">{t('settingsSection.basicSubtitle')}</p>
        
        <div className="settings-row">
          <span className="settings-label">{t('settingsSection.profilePicture')}</span>
          <div className="profile-picture-edit">
            <div className="small-avatar-wrapper" onClick={() => fileInputRef.current?.click()}>
              {displayUser?.profileImageUrl || displayUser?.profilePicture ? (
                <img src={displayUser.profileImageUrl || displayUser.profilePicture} alt="Profile" className="small-avatar" />
              ) : (
                <div className="small-avatar-placeholder">{userInitials}</div>
              )}
              {isUploadingImage && <div className="upload-spinner small"></div>}
            </div>
            <button className="change-photo-btn" onClick={() => fileInputRef.current?.click()}>
              {isUploadingImage ? 'Uploading...' : 'Change'}
            </button>
          </div>
        </div>

        <div className="settings-row">
          <span className="settings-label">{t('settingsSection.fullName')}</span>
          <span className="settings-value">{userName}</span>
        </div>

        <div className="settings-row">
          <span className="settings-label">{t('settingsSection.email')}</span>
          <div className="settings-value-with-badge">
            <span>{displayUser?.email || t('settingsSection.notSet')}</span>
            {(displayUser?.verified || displayUser?.emailVerified) && (
              <span className="verified-badge">{t('settingsSection.verified')}</span>
            )}
          </div>
        </div>

        <div className="settings-row">
          <span className="settings-label">{t('settingsSection.phone')}</span>
          <span className="settings-value">{displayUser?.phoneNumber || displayUser?.phone || t('settingsSection.notSet')}</span>
           </div>

        <div className="settings-row">
          <span className="settings-label">{t('settingsSection.nationality', { defaultValue: 'Nationality' })}</span>
          <span className="settings-value">{displayUser?.nationality || t('settingsSection.notSet')}</span>
        </div>

        <div className="settings-row">
          <span className="settings-label">{t('settingsSection.currentLocation', { defaultValue: 'Current location' })}</span>
          <span className="settings-value">{displayLocation || t('settingsSection.notSet')}</span>
        </div>

        <div className="settings-row">
          <span className="settings-label">{t('settingsSection.gender')}</span>
          <span className="settings-value">
            {displayUser?.gender
              ? displayUser.gender.charAt(0).toUpperCase() + displayUser.gender.slice(1)
              : t('settingsSection.notSpecified')}
          </span>
        </div>

        <div className="settings-row">
          <span className="settings-label">{t('settingsSection.dateOfBirth', { defaultValue: 'Date of birth' })}</span>
          <span className="settings-value">
            {displayUser?.dateOfBirth
              ? new Date(displayUser.dateOfBirth).toLocaleDateString()
              : t('settingsSection.notSet')}
          </span>
        </div>

        <button className="edit-profile-full-btn" onClick={() => setShowEditProfileModal(true)}>
          <Edit size={18} />
          Edit Profile Information
        </button>
      </div>

      <div className="settings-card">
        <h4>{t('settingsSection.security')}</h4>
        <button className="settings-action-btn" onClick={() => setShowPasswordModal(true)}>
          <Shield size={20} />
          <span>{t('settingsSection.changePassword')}</span>
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="settings-card danger">
        <h4>{t('settingsSection.dangerZone')}</h4>
        <p className="danger-warning">{t('settingsSection.dangerWarning')}</p>
        <button className="delete-account-btn" onClick={() => setShowDeleteModal(true)}>
          <Trash2 size={18} />
          {t('settingsSection.deleteAccount')}
        </button>
      </div>
    </div>
  );

  const handleProfileCompletionAction = () => {
    const { missing } = getProfileCompletion(displayUser, addresses);
    if (missing.length === 0) return;

    const targetTab = getProfileTabForMissingField(missing[0]);
    handleTabChange(targetTab);
    setSearchParams({ tab: targetTab, complete: '1' });

    if (targetTab === 'settings') {
      setShowEditProfileModal(true);
    } else if (targetTab === 'addresses') {
      setShowAddressModal(true);
    }
  };

  return (
    <div className="profile-page">
      <aside className="profile-sidebar">
        <nav className="profile-nav">
          <button className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => handleTabChange('overview')}>
            <User size={20} />
            <span>{t('overview')}</span>
          </button>
          <button className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => handleTabChange('orders')}>
            <Package size={20} />
            <span>{t('orders')}</span>
            {unreadOrderNotifications > 0 && (
              <span className="badge">{unreadOrderNotifications}</span>
            )}
          </button>
          <button className={`nav-item ${activeTab === 'wishlist' ? 'active' : ''}`} onClick={() => handleTabChange('wishlist')}>
            <Heart size={20} />
            <span>{t('wishlist')}</span>
            {wishlist?.length > 0 && <span className="badge">{wishlist.length}</span>}
          </button>
          <button className={`nav-item ${activeTab === 'bids' ? 'active' : ''}`} onClick={() => handleTabChange('bids')}>
            <MessageCircle size={20} />
            <span>{t('bids')}</span>
            {unreadOffersCount > 0 && (
              <span className="badge">{unreadOffersCount}</span>
            )}
          </button>
          <button className={`nav-item ${activeTab === 'addresses' ? 'active' : ''}`} onClick={() => handleTabChange('addresses')}>
            <MapPin size={20} />
            <span>{t('addresses')}</span>
          </button>
          <button className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => handleTabChange('settings')}>
            <Settings size={20} />
            <span>{t('settings')}</span>
          </button>
          <button className="nav-item logout" onClick={handleLogout}>
            <LogOut size={20} />
            <span>{t('signOut')}</span>
          </button>
        </nav>
      </aside>

      <main className="profile-main">
        <h1 className="page-title">
          {activeTab === 'overview' && t('pageTitle.overview')}
          {activeTab === 'orders' && t('pageTitle.orders')}
          {activeTab === 'wishlist' && t('pageTitle.wishlist')}
          {activeTab === 'bids' && t('pageTitle.bids')}
          {activeTab === 'addresses' && t('pageTitle.addresses')}
          {activeTab === 'settings' && t('pageTitle.settings')}
        </h1>

        <ProfileCompletionBanner
          user={displayUser}
          addresses={addresses}
          onComplete={handleProfileCompletionAction}
        />

        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'orders' && renderOrders()}
        {activeTab === 'wishlist' && renderWishlist()}
        {activeTab === 'bids' && renderBids()}
        {activeTab === 'addresses' && renderAddresses()}
        {activeTab === 'settings' && renderSettings()}
      </main>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content order-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('orderList.orderTitle', { number: selectedOrder.orderNumber || selectedOrder.id })}</h3>
              <button className="close-modal" onClick={() => setSelectedOrder(null)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="order-detail-content">
              <div className="order-detail-status">
                <div className={`order-status-badge large ${(selectedOrder.orderStatus || selectedOrder.status || '').toLowerCase().replace(/\s/g, '-')}`}>
                  {getStatusIcon(selectedOrder.orderStatus || selectedOrder.status)}
                  <span>{getStatusText(selectedOrder.orderStatus || selectedOrder.status)}</span>
                </div>
                <p className="order-detail-date">Placed on {formatDate(selectedOrder.createdAt)}</p>
                <p className="order-detail-date" style={{ marginTop: '0.5rem' }}>
                  Payment: {getPaymentStatusLabel(selectedOrder)}
                  {(selectedOrder.paymentMethod || '').toUpperCase() === 'CASH_ON_DELIVERY' &&
                    ` · ${t('payment.codLabel')}`}
                </p>
              </div>

              <div className="order-detail-items">
                <h4>{t('orderList.items')}</h4>
                {selectedOrder.items?.map((item, idx) => (
                  <div key={idx} className="order-detail-item">
                    <img src={getProductImageUrl(item.product || item)} alt="" className="item-image" />
                    <div className="item-info">
                      <p className="item-name">{item.product?.name || t('orderList.product')}</p>
                      <p className="item-variant">
                        {item.size && `Size: ${item.size}`}
                        {item.size && item.color && ' • '}
                        {item.color && `Color: ${item.color}`}
                      </p>
                      <p className="item-qty">Qty: {item.quantity || 1}</p>
                    </div>
                    <p className="item-price">{formatPrice(item.price || item.product?.price)}</p>
                  </div>
                ))}
              </div>

              <div className="order-detail-summary">
                <div className="summary-row">
                  <span>{t('orderList.subtotal')}</span>
                  <span>{formatPrice(selectedOrder.subtotal || selectedOrder.totalPrice)}</span>
                </div>
                <div className="summary-row">
                  <span>{t('orderList.shipping')}</span>
                  <span>{selectedOrder.shippingFee ? formatPrice(selectedOrder.shippingFee) : t('orderList.freeShipping')}</span>
                </div>
                <div className="summary-row total">
                  <span>{t('orderList.total')}</span>
                  <span>{formatPrice(selectedOrder.finalPrice || selectedOrder.totalPrice || selectedOrder.total)}</span>
                </div>
              </div>

              {selectedOrder.shippingAddress && (
                <div className="order-detail-address">
                  <h4>{t('orderList.shippingAddress')}</h4>
                  <p>{selectedOrder.shippingAddress.fullName}</p>
                  <p>{selectedOrder.shippingAddress.address || selectedOrder.shippingAddress.homeAddress}</p>
                  <p>{selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.country}</p>
                </div>
              )}

              {canBuyerCancelOrder(selectedOrder) && (
                <div className="order-detail-actions">
                  <button
                    type="button"
                    className="order-cancel-btn"
                    onClick={() => {
                      setCancelError('');
                      setCancelReason('');
                      setOrderToCancel(selectedOrder);
                    }}
                    disabled={isCancellingOrder}
                  >
                    <XCircle size={18} />
                    {t('orderCancel.cancelOrder')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {orderToCancel && (
        <div className="modal-overlay" onClick={() => !isCancellingOrder && setOrderToCancel(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('orderCancel.cancelConfirmTitle')}</h3>
              <button
                type="button"
                className="close-modal"
                onClick={() => setOrderToCancel(null)}
                disabled={isCancellingOrder}
              >
                <X size={24} />
              </button>
            </div>
            <div className="delete-warning">
              <XCircle size={48} className="warning-icon" />
              <p>{t('orderCancel.cancelConfirmMessage')}</p>
            </div>
            <div className="form-group" style={{ padding: '0 1.5rem' }}>
              <label htmlFor="cancel-reason">{t('orderCancel.cancelReasonLabel')}</label>
              <textarea
                id="cancel-reason"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={t('orderCancel.cancelReasonPlaceholder')}
                className="input-field"
                maxLength={500}
                disabled={isCancellingOrder}
              />
            </div>
            {cancelError && (
              <div className="error-message" style={{ margin: '0 1.5rem' }}>
                {cancelError}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setOrderToCancel(null)}
                disabled={isCancellingOrder}
              >
                {t('orderCancel.keepOrder')}
              </button>
              <button
                type="button"
                className="confirm-delete-btn"
                onClick={handleConfirmCancelOrder}
                disabled={isCancellingOrder}
              >
                {isCancellingOrder ? '...' : t('orderCancel.confirmCancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfileModal && (
        <div className="modal-overlay" onClick={() => setShowEditProfileModal(false)}>
          <div className="modal-content edit-profile-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('modals.editProfile')}</h3>
              <button className="close-modal" onClick={() => setShowEditProfileModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleProfileUpdate} className="profile-form">
              {profileError && <div className="error-message">{profileError}</div>}
              {profileSuccess && <div className="success-message">{t('accountMessages.profileUpdated')}</div>}
              
              <div className="profile-image-section">
                <div className="edit-avatar-container" onClick={() => fileInputRef.current?.click()}>
                  {user?.profileImageUrl || user?.profilePicture ? (
                    <img src={user.profileImageUrl || user.profilePicture} alt="Profile" className="edit-avatar-image" />
                  ) : (
                    <span className="edit-avatar-initials">{userInitials}</span>
                  )}
                  <div className="edit-avatar-overlay">
                    <Camera size={24} />
                    <span>Change Photo</span>
                  </div>
                  {isUploadingImage && <div className="upload-spinner large"></div>}
                </div>
                {(user?.profileImageUrl || user?.profilePicture) && (
                  <button 
                    type="button"
                    className="remove-photo-btn"
                    onClick={handleRemoveProfilePicture}
                    disabled={isUploadingImage}
                  >
                    <Trash2 size={16} />
                    Remove Photo
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input 
                    type="text" 
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm({...profileForm, firstName: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input 
                    type="text" 
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm({...profileForm, lastName: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input 
                  type="email" 
                  value={profileForm.email}
                  disabled
                  className="disabled"
                />
                <span className="form-hint">Email cannot be changed</span>
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input 
                  type="tel" 
                  value={profileForm.phoneNumber}
                  onChange={(e) => setProfileForm({...profileForm, phoneNumber: e.target.value})}
                  placeholder="+1234567890"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('settingsSection.nationality', { defaultValue: 'Nationality' })}</label>
                <input
                  type="text"
                  value={profileForm.nationality}
                  onChange={(e) => setProfileForm({ ...profileForm, nationality: e.target.value })}
                  placeholder={t('settingsSection.nationality', { defaultValue: 'Nationality' })}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('settingsSection.currentLocation', { defaultValue: 'Current location' })}</label>
                <input
                  type="text"
                  value={profileForm.currentLocation}
                  onChange={(e) => setProfileForm({ ...profileForm, currentLocation: e.target.value })}
                  placeholder={t('settingsSection.currentLocation', { defaultValue: 'Current location' })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Gender</label>
                <select 
                  value={profileForm.gender}
                  onChange={(e) => setProfileForm({...profileForm, gender: e.target.value})}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('settingsSection.dateOfBirth', { defaultValue: 'Date of birth' })}</label>
                <input
                  type="date"
                  value={profileForm.dateOfBirth}
                  onChange={(e) => setProfileForm({ ...profileForm, dateOfBirth: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                />
              </div>

              <button type="submit" className="submit-btn" disabled={isUpdatingProfile}>
                {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('modals.changePassword')}</h3>
              <button className="close-modal" onClick={() => setShowPasswordModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handlePasswordChange} className="password-form">
              {passwordError && <div className="error-message">{passwordError}</div>}
              {passwordSuccess && <div className="success-message">Password changed successfully!</div>}
              
              <div className="form-group">
                <label>Current Password</label>
                <input 
                  type="password" 
                  value={passwordForm.oldPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, oldPassword: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('modals.newPassword')}</label>
                <input 
                  type="password" 
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input 
                  type="password" 
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  required
                />
              </div>
              <button type="submit" className="submit-btn" disabled={isChangingPassword}>
                {isChangingPassword ? t('modals.changing') : t('modals.changePassword')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('modals.deleteAccount')}</h3>
              <button className="close-modal" onClick={() => setShowDeleteModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="delete-warning">
              <Trash2 size={48} className="warning-icon" />
              <p>{t('modals.deleteAccountConfirm')}</p>
              <p className="warning-text">{t('modals.deleteAccountWarning')}</p>
            </div>
            
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>{t('modals.cancel')}</button>
              <button className="confirm-delete-btn" onClick={handleDeleteAccount}>{t('modals.deleteAccount')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Address Modal */}
      {showAddressModal && (
        <div className="modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="modal-content address-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingAddress ? t('addressesSection.editTitle') : t('addressesSection.addTitle')}</h3>
              <button className="close-modal" onClick={() => setShowAddressModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleAddressSubmit} className="address-form">
              {addressError && <div className="error-message">{addressError}</div>}
              
              <div className="form-group">
                <label>Full Name *</label>
                <input 
                  type="text" 
                  value={addressForm.fullName}
                  onChange={(e) => setAddressForm({...addressForm, fullName: e.target.value})}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input 
                  type="tel" 
                  value={addressForm.phoneNumber}
                  onChange={(e) => setAddressForm({...addressForm, phoneNumber: e.target.value})}
                  placeholder="+1234567890"
                  required
                />
              </div>

              <div className="form-group">
                <label>Street Address *</label>
                <input 
                  type="text" 
                  value={addressForm.homeAddress}
                  onChange={(e) => setAddressForm({...addressForm, homeAddress: e.target.value})}
                  placeholder="123 Main Street, Apt 4B"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input 
                    type="text" 
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                    placeholder="New York"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Quartier *</label>
                  <select
                    value={addressForm.neighborhood}
                    onChange={(e) =>
                      setAddressForm({ ...addressForm, neighborhood: e.target.value })
                    }
                    required
                    disabled={isLoadingZones}
                  >
                    <option value="">
                      {isLoadingZones ? 'Chargement…' : 'Sélectionnez un quartier'}
                    </option>
                    {shippingZones.map((zone) => (
                      <option key={zone.id || zone.code} value={zone.code}>
                        {zone.name} — ${Number(zone.shippingFee).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Postal Code</label>
                  <input 
                    type="text" 
                    value={addressForm.postalCode}
                    onChange={(e) => setAddressForm({...addressForm, postalCode: e.target.value})}
                    placeholder="10001"
                  />
                </div>
                <div className="form-group">
                  <label>Country *</label>
                  <input 
                    type="text" 
                    value={addressForm.country}
                    onChange={(e) => setAddressForm({...addressForm, country: e.target.value})}
                    placeholder="United States"
                    required
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={addressForm.isDefault}
                    onChange={(e) => setAddressForm({...addressForm, isDefault: e.target.checked})}
                  />
                  <span>Set as default address</span>
                </label>
              </div>

              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isAddingAddress || isUpdatingAddress}
              >
                {isAddingAddress || isUpdatingAddress 
                  ? 'Saving...' 
                  : editingAddress 
                    ? 'Update Address' 
                    : 'Add Address'
                }
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Bid Modal */}
      {showBidModal && selectedBidProduct && (
        <BidModal
          product={selectedBidProduct}
          isOpen={showBidModal}
          onClose={() => {
            setShowBidModal(false);
            setSelectedBidProduct(null);
          }}
        />
      )}
      </div>
    );
  };
  
export default Profile;
